/* eslint-disable max-lines */
import { Fragment, useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

import type {
  BidDeliverable,
  ContractTerm,
  CriticalDate,
  CriticalDateUserRow,
  EvaluationCriterion,
  ExtractionCategoryStatus,
  ExtractionOverallStatus,
  PipelineFailureReason,
  ProcurementOverview,
  Requirement,
  RoleRequirement,
  SourceRef,
  SourceSnippet,
  SubmissionDeadlineState,
  TenderDocument,
} from "../../types"

import {
  type DeliverableDocumentApi,
  downloadExtractionDocx,
  downloadPmDocx,
  downloadSetupFilesZip,
  type ExtractionTimingMetric,
  fetchExtractionTiming,
  getTenderDocumentUrl,
  type PmContent,
  type TimelineEntryApi,
} from "@/api/tender-api"
import { FeedbackDialog } from "@/components/feedback-dialog"
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  FileDown,
  FileSearch,
  FileText,
  FileWarning,
  Info,
  Loader2,
  MoreHorizontal,
  RefreshCw,
  Star,
  StopCircle,
  XCircle,
} from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { CitationSidebarHeader } from "@/features/chat/components/citation-sidebar-header"
import { CitationViewerContent } from "@/features/chat/components/citation-viewer-content"
import { cn } from "@/lib/utils"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"

import { useUpdateExtractionCriticalDates } from "../../hooks/use-tender-extraction"
import { formatDeadline, isDateOnly, toDateInputValue } from "../../utils/deadline"
import { knownPage } from "../../utils/source-page"
import { CriticalDatesTabEditor } from "../critical-dates-tab"
import { ExtractionProgressPanel } from "../extraction-progress-panel"
import { CitationText } from "./extraction-citation"
import { buildExtractionLookup } from "./extraction-citation-utils"
import { LlmTraceModal } from "../llm-trace-modal"
import { SnabbanalysCard } from "../snabbanalys-card"
import { SnippetPopover } from "../snippet-card"
import { TenderAiDisclaimer } from "../tender-ai-disclaimer"

type SourceRefDisplayProps = {
  source: SourceRef
  onOpenPreview?: () => void
}

function SourceRefDisplay({ source, onOpenPreview }: SourceRefDisplayProps) {
  const { t } = useTranslation()
  const page = knownPage(source.page)
  const pageText =
    page === undefined ? "" : ` · ${t("tender.extraction.sourcePageAbbrev")}\u00a0${page}`
  if (source.kind === "file") {
    const label = `${source.documentName.replace(".pdf", "")}${pageText}`
    if (onOpenPreview) {
      return (
        <Button
          className="text-muted-foreground hover:text-foreground h-auto p-0 text-left text-xs font-normal"
          type="button"
          variant="link"
          onClick={onOpenPreview}
        >
          {label}
        </Button>
      )
    }
    return <span className="text-muted-foreground text-xs">{label}</span>
  }
  return (
    <span className="text-muted-foreground text-xs">
      {source.libraryName}: {source.documentName}
      {pageText}
    </span>
  )
}

function childrenToText(children: React.ReactNode): string {
  if (typeof children === "string") return children
  if (typeof children === "number") return String(children)
  if (Array.isArray(children)) return children.map(childrenToText).join("")
  if (children && typeof children === "object" && "props" in children) {
    const el = children as { props: { children?: React.ReactNode } }
    return childrenToText(el.props.children)
  }
  return ""
}

type ExtractionEmptyStateProps = {
  title: string
  description: string
  tone?: "default" | "warning"
  retryCategory?: string
  onRetryCategories?: (categories: string[]) => void
  canEdit?: boolean
}

function ExtractionEmptyState({
  title,
  description,
  tone = "default",
  retryCategory,
  onRetryCategories,
  canEdit = true,
}: ExtractionEmptyStateProps) {
  const { t } = useTranslation()
  const isWarning = tone === "warning"
  const containerClassName = isWarning
    ? "rounded-lg border border-warning bg-warning/[0.04] p-4"
    : "rounded-lg border border-border bg-muted/50 p-4"
  const titleClassName = isWarning
    ? "text-sm font-medium text-warning-foreground"
    : "text-sm font-medium"

  return (
    <div className={containerClassName}>
      <div className="flex items-start gap-3">
        {isWarning ? (
          <AlertTriangle className="text-warning mt-0.5 size-4 shrink-0" />
        ) : (
          <Info className="text-muted-foreground mt-0.5 size-4 shrink-0" />
        )}
        <div className="flex-1 space-y-1">
          <p className={titleClassName}>{title}</p>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>
        {canEdit && retryCategory && onRetryCategories ? (
          <Button size="sm" variant="outline" onClick={() => onRetryCategories([retryCategory])}>
            <RefreshCw className="mr-1.5 size-3.5" />
            {t("tender.extraction.retry")}
          </Button>
        ) : null}
      </div>
    </div>
  )
}

type CategoryFailureBannerProps = {
  category: string
  categoryLabel: string
  errorMessage?: string
  retryCount: number
  caseId: string
  runId: string
  onRetry?: (categories: string[]) => void
  canEdit?: boolean
}

function CategoryFailureBanner({
  category,
  categoryLabel,
  errorMessage,
  retryCount,
  caseId,
  runId,
  onRetry,
  canEdit = true,
}: CategoryFailureBannerProps) {
  const { t } = useTranslation()
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const showBugReport = retryCount >= 1

  return (
    <div className="border-warning bg-warning/[0.04] mb-4 rounded-lg border p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="text-warning mt-0.5 size-4 shrink-0" />
        <div className="flex-1 space-y-1">
          <p className="text-warning-foreground text-sm font-medium">
            {category === "pm_content"
              ? t("tender.extraction.summaryExtractFailedTitle")
              : t("tender.extraction.categoryExtractFailedTitle", { category: categoryLabel })}
          </p>
          <p className="text-muted-foreground text-xs">
            {t("tender.extraction.categoryFailureDescription")}
          </p>
          {showBugReport && (
            <p className="text-muted-foreground text-xs">
              {t("tender.extraction.categoryFailedHint")}
            </p>
          )}
        </div>
        {canEdit && (
          <div className="flex shrink-0 items-center gap-2">
            {showBugReport && (
              <Button size="sm" variant="ghost" onClick={() => setFeedbackOpen(true)}>
                {t("tender.extraction.reportBug")}
              </Button>
            )}
            {onRetry && (
              <Button size="sm" variant="outline" onClick={() => onRetry([category])}>
                <RefreshCw className="mr-1.5 size-3.5" />
                {t("tender.extraction.regenerate")}
              </Button>
            )}
          </div>
        )}
      </div>
      {showBugReport && (
        <FeedbackDialog
          hideTrigger
          lockLane
          initialLane="bug"
          open={feedbackOpen}
          prefillContext={{
            errorId: errorMessage ?? null,
            sessionId: runId,
            messageId: `${caseId}/${runId}/${category}`,
            responseContent: `Tender extraction category "${category}" failed${
              errorMessage ? `: ${errorMessage}` : ""
            } (retry ${retryCount}x).`,
          }}
          onOpenChange={setFeedbackOpen}
        />
      )}
    </div>
  )
}

const CONFIDENCE_CONFIG = {
  saker: {
    labelKey: "tender.confidence.saker",
    className: "bg-success/15 text-success border-success",
  },
  osaker: {
    labelKey: "tender.confidence.osaker",
    className: "bg-warning/10 text-warning-foreground border-warning",
  },
  ej_verifierbar: {
    labelKey: "tender.confidence.ej_verifierbar",
    className: "bg-destructive/10 text-destructive border-destructive",
  },
}

const RISK_CONFIG = {
  lag: {
    labelKey: "tender.risk.lag",
    className: "bg-success/15 text-success border-success",
  },
  medel: {
    labelKey: "tender.risk.medel",
    className: "bg-warning/10 text-warning-foreground border-warning",
  },
  hog: {
    labelKey: "tender.risk.hog",
    className: "bg-destructive/10 text-destructive border-destructive",
  },
}

type ExtractionSectionProps = {
  caseId: string
  runId: string
  caseTitle: string
  documents: TenderDocument[]
  requirements: Requirement[]
  evaluationCriteria: EvaluationCriterion[]
  contractTerms: ContractTerm[]
  criticalDates: CriticalDate[]
  criticalDatesRemovedIds?: string[]
  criticalDatesUserRows?: CriticalDateUserRow[]
  extractedCriticalDateIds?: string[]
  roleRequirements: RoleRequirement[]
  bidDeliverables: BidDeliverable[]
  deliverableDocuments?: DeliverableDocumentApi[]
  timeline?: TimelineEntryApi[]
  bidSummary?: { summary: string } | null
  pmContent?: PmContent | null
  procurementOverview?: ProcurementOverview
  submissionDeadline?: SubmissionDeadlineState
  /** The case's "Sista dag för frågor" (YYYY-MM-DD), when one was entered. */
  questionsDeadline?: string | null
  categoryStatus?: Record<string, ExtractionCategoryStatus>
  categoryErrors?: Record<string, string | undefined>
  categoryRetryCount?: Record<string, number | undefined>
  overallStatus?: ExtractionOverallStatus
  rateLimitRetry?: {
    attempt: number
    nextRetryAt: string
    phase: string
    sleepSeconds: number
  } | null
  error?: string
  failureReason?: PipelineFailureReason
  cancelRequested?: boolean
  onRetryExtraction?: () => void
  onRetryCategories?: (categories: string[]) => void
  onAbort?: () => void
  isAborting?: boolean
  onUpdateSubmissionDeadline?: (submissionDeadline: string | null, reason?: string) => Promise<void>
  isUpdatingSubmissionDeadline?: boolean
  canEdit?: boolean
}

function getPipelineFailureMessage(
  t: (key: string) => string,
  failureReason?: PipelineFailureReason
) {
  if (failureReason === "user_cancelled") return t("tender.abort.failureReasons.user_cancelled")
  if (failureReason === "worker_lost") return t("tender.abort.failureReasons.worker_lost")
  if (failureReason === "hard_timeout") return t("tender.abort.failureReasons.hard_timeout")
  if (failureReason === "rate_limited_exhausted")
    return t("tender.abort.failureReasons.rate_limited_exhausted")
  // Pipeline errors may contain model, schema, or transport details. Keep
  // those in the stored run and feedback report, not in the customer-facing
  // recovery message.
  return t("tender.abort.failureReasons.pipeline_error")
}

const PM_QUICK_OVERVIEW_FIELDS = [
  { key: "upphandling", label: "Upphandling" },
  { key: "avtalstid", label: "Avtalstid" },
  { key: "volym", label: "Volym" },
  { key: "personnel", label: "Personalbehov" },
] as const

function PmMarkdown({
  source,
  extractionLookup,
  openDocumentPreview,
}: {
  source: string
  extractionLookup: ReturnType<typeof buildExtractionLookup>
  openDocumentPreview: (source: SourceRef) => void
}) {
  return (
    <div className="prose dark:prose-invert prose-sm max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => (
            <p>
              <CitationText
                as="span"
                lookup={extractionLookup}
                text={childrenToText(children)}
                onOpenSource={(s) => openDocumentPreview(s)}
              />
            </p>
          ),
          li: ({ children }) => (
            <li>
              <CitationText
                as="span"
                lookup={extractionLookup}
                text={childrenToText(children)}
                onOpenSource={(s) => openDocumentPreview(s)}
              />
            </li>
          ),
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  )
}

function PmSectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="mt-2 mb-3 text-base font-semibold">{children}</h3>
}

type PmContentSectionsProps = {
  pmContent: PmContent
  procurementOverview?: ProcurementOverview
  questionsDeadline?: string | null
  deliverableDocuments: DeliverableDocumentApi[]
  roleRequirements: RoleRequirement[]
  evaluationCriteria: EvaluationCriterion[]
  contractTerms: ContractTerm[]
  timeline: TimelineEntryApi[]
  extractionLookup: ReturnType<typeof buildExtractionLookup>
  openDocumentPreview: (source: SourceRef) => void
}

function PmIntro({
  overview,
  questionsDeadline,
}: {
  overview: ProcurementOverview
  questionsDeadline?: string | null
}) {
  const facts: { label: string; value: string }[] = []
  if (overview.client) facts.push({ label: "Beställare", value: overview.client })
  if (overview.estimatedValue)
    facts.push({ label: "Estimerat värde", value: overview.estimatedValue })
  if (overview.positionsCount != null)
    facts.push({ label: "Antal positioner", value: String(overview.positionsCount) })
  if (overview.submissionDeadline)
    facts.push({ label: "Sista anbudsdag", value: overview.submissionDeadline })
  if (questionsDeadline) facts.push({ label: "Sista dag för frågor", value: questionsDeadline })

  if (!overview.description && facts.length === 0) return null

  return (
    <div className="bg-accent/40 space-y-3 rounded-lg border p-4">
      {overview.description && <p className="text-sm leading-relaxed">{overview.description}</p>}
      {facts.length > 0 && (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
          {facts.map(({ label, value }) => (
            <div key={label} className="flex flex-col">
              <dt className="text-muted-foreground text-xs">{label}</dt>
              <dd className="text-sm font-medium">{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}

function PmContentSections({
  pmContent,
  procurementOverview,
  questionsDeadline,
  deliverableDocuments,
  roleRequirements,
  evaluationCriteria,
  contractTerms,
  timeline,
  extractionLookup,
  openDocumentPreview,
}: PmContentSectionsProps) {
  const overview = pmContent.quick_overview
  const overviewFields = PM_QUICK_OVERVIEW_FIELDS.filter(({ key }) => overview[key].trim())

  const evaluationInterpretation = pmContent.evaluation_interpretation.trim()
  const otherTermsSummary = pmContent.other_terms_summary.trim()

  const highRiskTerms = contractTerms.filter((t) => t.riskLevel === "hog" || t.flagged)
  const mediumRiskTerms = contractTerms.filter((t) => t.riskLevel === "medel" && !t.flagged)

  return (
    <div className="space-y-6">
      {/* Intro: prose summary + key facts pulled straight from procurementOverview
          (mirrors the snabbanalys card the user already sees). No LLM round-trip
          — these fields are already extracted, so we just inline them. */}
      {procurementOverview && (
        <PmIntro overview={procurementOverview} questionsDeadline={questionsDeadline} />
      )}

      {/* Infobox */}
      {overviewFields.length > 0 && (
        <div className="bg-accent/40 rounded-lg border p-4">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-[max-content_1fr] sm:gap-x-4 sm:gap-y-2">
            {overviewFields.map(({ key, label }) => {
              const value = overview[key]
              return (
                <Fragment key={key}>
                  <dt className="text-muted-foreground text-sm font-semibold">{label}</dt>
                  <dd className="text-sm">
                    <PmMarkdown
                      extractionLookup={extractionLookup}
                      openDocumentPreview={openDocumentPreview}
                      source={value}
                    />
                  </dd>
                </Fragment>
              )
            })}
          </dl>
        </div>
      )}

      {/* 1. Dokument att lämna in (deliverable_documents) */}
      {deliverableDocuments.length > 0 && (
        <section>
          <PmSectionHeading>1. Dokument att lämna in</PmSectionHeading>
          <ul className="space-y-2 text-sm">
            {deliverableDocuments.map((doc) => (
              <li key={doc.id}>
                <span className="font-semibold">
                  {doc.isRequired ? "" : "(Frivilligt) "}
                  {doc.documentName}
                </span>
                {doc.description && (
                  <span className="text-muted-foreground"> — {doc.description}</span>
                )}
                {doc.mustInclude.length > 0 && (
                  <ul className="mt-1 list-disc pl-5">
                    {doc.mustInclude.map((item, i) => (
                      <li key={`${doc.id}-mi-${i}`}>{item}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* 2. Krav på nyckelpersoner */}
      {roleRequirements.length > 0 &&
        (() => {
          // Erfarenhet: always hidden — most procurements bury "X years"
          // inside the prose instead of as a separate field, so the column
          // was sparse. Antal: folded into a small "N×" pill next to the
          // role name (only when > 1 — avoids "1×" noise on the common
          // case). Utbildning: hidden when uniformly empty.
          const showUtbildning = roleRequirements.some(
            (r) => (r.educationLevel ?? "").trim().length > 0
          )
          return (
            <section>
              <PmSectionHeading>2. Krav på nyckelpersoner</PmSectionHeading>
              <div className="overflow-hidden rounded-lg border">
                <table className="w-full table-fixed text-sm">
                  <thead className="bg-muted/50">
                    <tr className="text-left">
                      <th className="w-[16%] px-3 py-2 font-semibold">Roll</th>
                      {showUtbildning && (
                        <th className="w-[18%] px-3 py-2 font-semibold">Utbildning</th>
                      )}
                      <th className="px-3 py-2 font-semibold">Specifika krav</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roleRequirements.map((role) => (
                      <tr key={role.id} className="border-t align-top">
                        <td className="px-3 py-2 font-semibold">
                          <div className="flex flex-col items-start gap-1">
                            <span>{role.roleName}</span>
                            {role.quantity > 1 && (
                              <Badge
                                className="px-2 py-0.5 text-xs font-normal tabular-nums"
                                variant="secondary"
                              >
                                {role.quantity}×
                              </Badge>
                            )}
                          </div>
                        </td>
                        {showUtbildning && (
                          <td className="px-3 py-2">{role.educationLevel ?? "—"}</td>
                        )}
                        <td className="text-muted-foreground space-y-1 px-3 py-2">
                          {role.requiredCertifications.length > 0 && (
                            <div>
                              <span className="text-foreground font-semibold">
                                Utbildning &amp; certifikat:
                              </span>{" "}
                              {role.requiredCertifications.join("; ")}
                            </div>
                          )}
                          {role.requiredExperience.length > 0 && (
                            <div>
                              <span className="text-foreground font-semibold">Erfarenhet:</span>{" "}
                              {role.requiredExperience.join("; ")}
                            </div>
                          )}
                          {role.meritingQualifications.length > 0 && (
                            <div>
                              <span className="text-foreground font-semibold">Övrigt:</span>{" "}
                              {role.meritingQualifications.join("; ")}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )
        })()}

      {/* 3. Tidslinje */}
      {timeline.length > 0 && (
        <section>
          <PmSectionHeading>3. Tidslinje</PmSectionHeading>
          <TimelineView entries={timeline} />
        </section>
      )}

      {/* 4. Utvärderingsmodell & vinstlogik */}
      {(evaluationCriteria.length > 0 || evaluationInterpretation) && (
        <section>
          <PmSectionHeading>4. Utvärderingsmodell &amp; vinstlogik</PmSectionHeading>
          {evaluationCriteria.length > 0 && (
            <div className="mb-3 overflow-hidden rounded-lg border">
              <table className="w-full table-fixed text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-left">
                    <th className="w-1/5 px-3 py-2 font-semibold">Kriterium</th>
                    <th className="px-3 py-2 font-semibold">Beskrivning</th>
                  </tr>
                </thead>
                <tbody>
                  {evaluationCriteria.map((c) => (
                    <tr key={c.id} className="border-t align-top">
                      <td className="px-3 py-2 font-semibold">{c.name}</td>
                      <td className="text-muted-foreground px-3 py-2">{c.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {evaluationInterpretation && (
            <PmMarkdown
              extractionLookup={extractionLookup}
              openDocumentPreview={openDocumentPreview}
              source={evaluationInterpretation}
            />
          )}
        </section>
      )}

      {/* 5. Viktiga avtalsvillkor & risker */}
      {(contractTerms.length > 0 || otherTermsSummary) && (
        <section>
          <PmSectionHeading>5. Viktiga avtalsvillkor &amp; risker</PmSectionHeading>
          {highRiskTerms.length > 0 && (
            <div className="mb-3">
              <h4 className="mb-1.5 text-sm font-semibold">Topp risker</h4>
              <PmRiskTable terms={highRiskTerms.slice(0, 10)} />
            </div>
          )}
          {mediumRiskTerms.length > 0 && (
            <div className="mb-3">
              <h4 className="mb-1.5 text-sm font-semibold">
                Övriga kommersiella förutsättningar (medelrisk)
              </h4>
              <PmRiskTable terms={mediumRiskTerms.slice(0, 10)} />
            </div>
          )}
          {highRiskTerms.length === 0 &&
            mediumRiskTerms.length === 0 &&
            contractTerms.length > 0 && <PmRiskTable terms={contractTerms.slice(0, 10)} />}
          {otherTermsSummary && (
            <div className="mt-3">
              <h4 className="mb-1.5 text-sm font-semibold">Övriga villkor</h4>
              <PmMarkdown
                extractionLookup={extractionLookup}
                openDocumentPreview={openDocumentPreview}
                source={otherTermsSummary}
              />
            </div>
          )}
        </section>
      )}
    </div>
  )
}

// Match "Avviker från ABK 09:" / "Avviker från AB 04 — " etc. — the column
// header already says "Avvikelse från standardavtal" and ABK 09 is the
// implied default for consulting procurements, so the prefix is redundant.
const DEVIATION_PREFIX_RE = /^Avviker fr[åa]n[^:—-]*[:—-]\s*/i

function stripDeviationPrefix(text: string): string {
  return text.replace(DEVIATION_PREFIX_RE, "")
}

function pmRiskDescriptionText(term: ContractTerm): string {
  // Risk descriptions are anchored in standard-contract deviations now.
  // Prefer the framework_deviation copy when present (the LLM is asked to
  // phrase it as "X i stället för ABK 09:s Y"); fall back to the plain
  // description for terms that don't have a recorded deviation.
  // Strip any "Avviker från {standard}:" prefix the LLM may have added —
  // the column header already says it.
  const dev = term.frameworkDeviation
  const raw = dev?.deviationDescription.trim() || term.description
  return stripDeviationPrefix(raw)
}

function PmRiskTable({ terms }: { terms: ContractTerm[] }) {
  // Risk column dropped — the section heading ("Topp risker" /
  // "Övriga kommersiella förutsättningar (medelrisk)") already conveys
  // severity, and every row in the body says "Avviker från ABK 09: ..."
  // anyway, so a per-row Hög/Medel badge was redundant. Flag (⚑) prefix
  // dropped for the same reason — flagged is by definition true for the
  // high-risk table and false for the medium-risk one.
  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full table-fixed text-sm">
        <thead className="bg-muted/50">
          <tr className="text-left">
            <th className="w-1/5 px-3 py-2 font-semibold">Villkor</th>
            <th className="px-3 py-2 font-semibold">Avvikelse från standardavtal</th>
          </tr>
        </thead>
        <tbody>
          {terms.map((t) => (
            <tr key={t.id} className="border-t align-top">
              <td className="px-3 py-2 font-semibold">{t.clause}</td>
              <td className="text-muted-foreground px-3 py-2">{pmRiskDescriptionText(t)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function formatTimingDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  const totalSeconds = Math.round(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes === 0) return `${seconds}s`
  return `${minutes}m ${seconds}s`
}

function timingDurationColor(ms: number): string {
  if (ms > 120_000) return "text-destructive"
  if (ms > 30_000) return "text-warning"
  return "text-success"
}

const TIMELINE_PHASE_ORDER = [
  "pre_submission",
  "submission",
  "evaluation",
  "contract_start",
  "contract",
  "post_contract",
] as const

const TIMELINE_PHASE_LABELS: Record<(typeof TIMELINE_PHASE_ORDER)[number], string> = {
  pre_submission: "Före anbud",
  submission: "Anbud",
  evaluation: "Utvärdering",
  contract_start: "Avtalsstart",
  contract: "Under avtalet",
  post_contract: "Efter avtalet",
}

function urgencyDotClass(urgency: string): string {
  if (urgency === "kritisk") return "bg-destructive-ink"
  if (urgency === "varning") return "bg-warning-ink"
  return "bg-success-ink"
}

type DateRelativeStatus = "passed" | "today" | "soon" | "upcoming"

function dateRelativeStatus(isoDate: string | null | undefined): DateRelativeStatus | null {
  // Concrete-date timeline entries get a date-relative status badge instead
  // of the LLM's urgency rating. "kritisk/varning/normal" added little —
  // what the bid manager actually needs to know is whether the date has
  // passed, is imminent, or is far enough out to ignore for now.
  if (!isoDate) return null
  const parsed = Date.parse(isoDate)
  if (Number.isNaN(parsed)) return null
  const target = new Date(parsed)
  target.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const msPerDay = 24 * 60 * 60 * 1000
  const diffDays = Math.round((target.getTime() - today.getTime()) / msPerDay)
  if (diffDays < 0) return "passed"
  if (diffDays === 0) return "today"
  if (diffDays <= 14) return "soon"
  return "upcoming"
}

const DATE_STATUS_LABELS: Record<DateRelativeStatus, string> = {
  passed: "Passerat",
  today: "Idag",
  soon: "Snart",
  upcoming: "Kommande",
}

function dateStatusBadgeVariant(
  status: DateRelativeStatus
): "destructive" | "default" | "secondary" {
  // "soon" and "today" warrant attention; "passed" and far-future are muted.
  if (status === "today") return "destructive"
  if (status === "soon") return "default"
  return "secondary"
}

function dateStatusDotClass(status: DateRelativeStatus | null, urgency: string): string {
  // Dot color follows date status when we have a concrete date; falls back
  // to LLM urgency for entries with only a date_text anchor.
  if (status === "today") return "bg-destructive-ink"
  if (status === "soon") return "bg-warning-ink"
  if (status === "passed") return "bg-muted-foreground/40"
  if (status === "upcoming") return "bg-success-ink"
  return urgencyDotClass(urgency)
}

function groupTimelineByPhase(
  entries: TimelineEntryApi[]
): { phase: string; entries: TimelineEntryApi[] }[] {
  const buckets = new Map<string, TimelineEntryApi[]>()
  for (const entry of entries) {
    const phase = TIMELINE_PHASE_ORDER.includes(
      entry.phase as (typeof TIMELINE_PHASE_ORDER)[number]
    )
      ? entry.phase
      : "pre_submission"
    const existing = buckets.get(phase) ?? []
    existing.push(entry)
    buckets.set(phase, existing)
  }
  return TIMELINE_PHASE_ORDER.filter((p) => buckets.has(p)).map((p) => ({
    phase: p,
    entries: buckets.get(p) ?? [],
  }))
}

function TimelineView({ entries }: { entries: TimelineEntryApi[] }) {
  const groups = groupTimelineByPhase(entries)
  return (
    <ol className="border-border relative ml-3 border-l">
      {groups.map((group) => {
        const phaseLabel =
          TIMELINE_PHASE_LABELS[group.phase as keyof typeof TIMELINE_PHASE_LABELS] || group.phase
        return (
          <Fragment key={group.phase}>
            {/* Phase as a non-bulleted divider so it doesn't read as its own
                timeline event. The line continues; the label sits in the
                content column above the first entry of the phase. */}
            <li className="relative mt-6 mb-2 ml-6 list-none first:mt-0">
              <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                {phaseLabel}
              </span>
            </li>
            {group.entries.map((entry) => {
              const status = dateRelativeStatus(entry.date)
              return (
                <li key={entry.id} className="relative mb-5 ml-6">
                  <span
                    className={cn(
                      "ring-background absolute -left-[26px] mt-1.5 size-3 rounded-full ring-4",
                      dateStatusDotClass(status, entry.urgency)
                    )}
                  />
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold">{entry.title}</h4>
                      {entry.description && (
                        <p className="text-muted-foreground mt-1 text-sm">{entry.description}</p>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-sm font-medium tabular-nums">
                        {entry.date ?? entry.dateText ?? "—"}
                      </div>
                      {status && (
                        <Badge className="mt-1" variant={dateStatusBadgeVariant(status)}>
                          {DATE_STATUS_LABELS[status]}
                        </Badge>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </Fragment>
        )
      })}
    </ol>
  )
}

/**
 * Per-tab Regenerate button. Sends a single category via onRetryCategories;
 * the backend cascades to dependent synthesis steps (e.g. requirements →
 * role_requirements → pm_content) so users only have to think about the
 * primary tab they're regenerating.
 */
function TabRegenerateButton({
  category,
  status,
  onRetry,
  canEdit,
}: {
  category: string
  status: ExtractionCategoryStatus | undefined
  onRetry?: (categories: string[]) => void
  canEdit: boolean
}) {
  const { t } = useTranslation()
  if (!canEdit || !onRetry) return null
  const isExtracting = status === "extracting" || status === "verifying" || status === "pending"
  return (
    <Button
      className="self-end"
      disabled={isExtracting}
      size="sm"
      variant="ghost"
      onClick={() => onRetry([category])}
    >
      <RefreshCw className={`mr-1.5 size-3.5 ${isExtracting ? "animate-spin" : ""}`} />
      {t("tender.extraction.regenerateTab")}
    </Button>
  )
}

export function ExtractionSection({
  caseId,
  runId,
  caseTitle,
  documents,
  requirements,
  evaluationCriteria,
  contractTerms,
  criticalDates,
  criticalDatesRemovedIds = [],
  criticalDatesUserRows = [],
  extractedCriticalDateIds = [],
  roleRequirements,
  bidDeliverables,
  deliverableDocuments = [],
  timeline = [],
  bidSummary,
  pmContent,
  procurementOverview,
  submissionDeadline,
  questionsDeadline = null,
  categoryStatus = {},
  categoryErrors = {},
  categoryRetryCount = {},
  overallStatus,
  rateLimitRetry,
  failureReason,
  cancelRequested = false,
  onRetryExtraction,
  onRetryCategories,
  onAbort,
  isAborting = false,
  onUpdateSubmissionDeadline,
  isUpdatingSubmissionDeadline = false,
  canEdit = true,
}: ExtractionSectionProps) {
  const { t, i18n } = useTranslation()
  const updateCriticalDates = useUpdateExtractionCriticalDates(caseId)
  const [isEditingSubmissionDeadline, setIsEditingSubmissionDeadline] = useState(false)
  const [submissionDeadlineDraft, setSubmissionDeadlineDraft] = useState("")
  const [submissionDeadlineReason, setSubmissionDeadlineReason] = useState("")
  const [submissionDeadlineError, setSubmissionDeadlineError] = useState<string | null>(null)
  const docNameToId = useMemo(() => {
    const map = new Map<string, string>()
    for (const doc of documents) {
      map.set(doc.filename, doc.id)
    }
    return map
  }, [documents])

  const openDocumentPreview = useCallback(
    (source: SourceRef | SourceSnippet) => {
      if (source.kind !== "file" || !caseId) return
      const docId = source.documentId || docNameToId.get(source.documentName)
      if (!docId) return
      const documentUrl = getTenderDocumentUrl({
        caseId,
        docId,
        filename: source.documentName,
        page: source.page,
      })
      const title = source.documentName
      useContentSidebarStore.getState().openPanel({
        id: "tender-document-preview",
        title,
        headerMode: "custom",
        renderHeader: (context) => (
          <CitationSidebarHeader
            closeable={context.closeable}
            downloadUrl={documentUrl}
            isFocusable={context.isFocusable}
            isFocused={context.isFocused}
            title={title}
            onClose={context.onClose}
            onToggleFocus={context.onToggleFocus}
          />
        ),
        content: (
          <CitationViewerContent
            chunkSnippet={"chunkText" in source ? (source.chunkText ?? "") : ""}
            documentUrl={documentUrl}
            quoteSnippet={"quote" in source ? source.quote : ""}
          />
        ),
        focusable: true,
        closeable: true,
        resizable: true,
        isDirty: () => false,
      })
    },
    [caseId, docNameToId]
  )

  const getSnippetDocumentUrl = useCallback(
    (snippet: SourceSnippet): string | undefined => {
      if (snippet.kind !== "file" || !caseId) return undefined
      const docId = snippet.documentId || docNameToId.get(snippet.documentName)
      if (!docId) return undefined
      return getTenderDocumentUrl({
        caseId,
        docId,
        filename: snippet.documentName,
        page: snippet.page,
      })
    },
    [caseId, docNameToId]
  )

  const mandatoryCount = requirements.filter((r) => r.isMandatory).length
  const meritingCount = requirements.filter((r) => !r.isMandatory).length

  const requirementsStatus = categoryStatus["requirements"]
  const criteriaStatus = categoryStatus["evaluation_criteria"]
  const termsStatus = categoryStatus["contract_terms"]
  const datesStatus = categoryStatus["critical_dates"]
  const rolesStatus = categoryStatus["role_requirements"]
  const deliverablesStatus = categoryStatus["bid_deliverables"]
  const deliverableDocumentsStatus = categoryStatus["deliverable_documents"]
  const timelineStatus = categoryStatus["timeline"]
  const summaryStatus = categoryStatus["pm_content"] ?? categoryStatus["bid_summary"]

  const hasPmContent = Boolean(
    pmContent &&
    (pmContent.quick_overview.upphandling.trim() ||
      pmContent.quick_overview.avtalstid.trim() ||
      pmContent.quick_overview.volym.trim() ||
      pmContent.quick_overview.personnel.trim() ||
      pmContent.evaluation_interpretation.trim() ||
      pmContent.other_terms_summary.trim())
  )
  const summaryMarkdown = hasPmContent ? null : bidSummary?.summary.trim() || null

  useEffect(() => {
    if (isEditingSubmissionDeadline) return
    setSubmissionDeadlineDraft(
      toDateInputValue(submissionDeadline?.override?.value ?? submissionDeadline?.effectiveValue)
    )
    setSubmissionDeadlineReason(submissionDeadline?.override?.reason ?? "")
    setSubmissionDeadlineError(null)
  }, [isEditingSubmissionDeadline, submissionDeadline])

  const handleStartSubmissionDeadlineEdit = useCallback(() => {
    setSubmissionDeadlineDraft(
      toDateInputValue(submissionDeadline?.override?.value ?? submissionDeadline?.effectiveValue)
    )
    setSubmissionDeadlineReason(submissionDeadline?.override?.reason ?? "")
    setSubmissionDeadlineError(null)
    setIsEditingSubmissionDeadline(true)
  }, [submissionDeadline])

  const handleCancelSubmissionDeadlineEdit = useCallback(() => {
    setSubmissionDeadlineDraft(
      toDateInputValue(submissionDeadline?.override?.value ?? submissionDeadline?.effectiveValue)
    )
    setSubmissionDeadlineReason(submissionDeadline?.override?.reason ?? "")
    setSubmissionDeadlineError(null)
    setIsEditingSubmissionDeadline(false)
  }, [submissionDeadline])

  const handleSaveSubmissionDeadline = useCallback(async () => {
    if (!onUpdateSubmissionDeadline) return
    if (!submissionDeadlineDraft) {
      setSubmissionDeadlineError(t("tender.extraction.deadline.errorNeedDate"))
      return
    }
    if (!isDateOnly(submissionDeadlineDraft)) {
      setSubmissionDeadlineError(t("tender.extraction.deadline.errorInvalidFormat"))
      return
    }

    setSubmissionDeadlineError(null)
    await onUpdateSubmissionDeadline(submissionDeadlineDraft, submissionDeadlineReason)
    setIsEditingSubmissionDeadline(false)
  }, [onUpdateSubmissionDeadline, submissionDeadlineDraft, submissionDeadlineReason, t])

  const handleClearSubmissionDeadline = useCallback(async () => {
    if (!onUpdateSubmissionDeadline) return
    setSubmissionDeadlineError(null)
    await onUpdateSubmissionDeadline(null)
    setIsEditingSubmissionDeadline(false)
  }, [onUpdateSubmissionDeadline])

  const summaryReady = summaryStatus === "completed" && (!!hasPmContent || !!summaryMarkdown)

  const extractionLookup = useMemo(
    () =>
      buildExtractionLookup({
        requirements,
        evaluationCriteria,
        contractTerms,
        criticalDates,
        roleRequirements,
        bidDeliverables,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any),
    [
      requirements,
      evaluationCriteria,
      contractTerms,
      criticalDates,
      roleRequirements,
      bidDeliverables,
    ]
  )

  const isLoading = (status: ExtractionCategoryStatus | undefined) =>
    status === "extracting" || status === "pending" || status === "verifying"

  const [panelVisible, setPanelVisible] = useState(false)
  const [traceModalOpen, setTraceModalOpen] = useState(false)
  const [timingModalOpen, setTimingModalOpen] = useState(false)
  const [timingMetrics, setTimingMetrics] = useState<ExtractionTimingMetric[] | null>(null)
  const [timingVersion, setTimingVersion] = useState<string>("")
  const [timingLoading, setTimingLoading] = useState(false)
  const [isExportingPm, setIsExportingPm] = useState(false)
  const [isDownloadingSetup, setIsDownloadingSetup] = useState(false)

  useEffect(() => {
    if (overallStatus === "processing" || overallStatus === "pending") {
      setPanelVisible(true)
    } else if (overallStatus === "failed") {
      setPanelVisible(true)
    } else if (overallStatus === "completed" || overallStatus === "partial") {
      const id = setTimeout(() => setPanelVisible(false), 1500)
      return () => clearTimeout(id)
    }
  }, [overallStatus])

  const failedCategories = Object.entries(categoryStatus)
    .filter(([, status]) => status === "failed")
    .map(([cat]) => cat)

  const CATEGORY_LABEL_MAP = useMemo(
    () => ({
      requirements: t("tender.extraction.categories.requirements"),
      evaluation_criteria: t("tender.extraction.categories.evaluation_criteria"),
      contract_terms: t("tender.extraction.categories.contract_terms"),
      critical_dates: t("tender.extraction.categories.critical_dates"),
      role_requirements: t("tender.extraction.categories.role_requirements"),
      bid_deliverables: t("tender.extraction.categories.bid_deliverables"),
      deliverable_documents: t("tender.extraction.categories.deliverable_documents"),
      timeline: t("tender.extraction.categories.timeline"),
      pm_content: t("tender.extraction.categories.pm_content"),
    }),
    [t]
  )
  // A category can retain validated rows while reporting dropped invalid rows
  // in categoryErrors. Treat those as a recoverable partial result too, rather
  // than fading the only user-visible notice after the progress panel closes.
  const retryCategories = Array.from(
    new Set([...failedCategories, ...Object.keys(categoryErrors)])
  ).filter((category) => category in CATEGORY_LABEL_MAP)
  // Older runs may have been persisted as completed despite failed categories.
  // Keep their recovery action visible while new runs use the explicit status.
  const hasPartialFailure =
    retryCategories.length > 0 && (overallStatus === "partial" || overallStatus === "completed")
  // Runs analysed before newer categories existed have no entry for them, and
  // the handoff/summary readiness gate refuses such runs. A targeted retry of
  // only the missing categories (the cascade adds the summary) fills the gap
  // from data the run already stores, without a full rerun.
  // Same invariant as the server's readiness gate: every category must be
  // completed or skipped. Absent keys and non-terminal states (pending,
  // extracting, verifying) on a completed run are both legacy inconsistencies
  // that a targeted retry repairs; failed ones belong to the partial banner.
  const legacyWithoutCategoryState =
    overallStatus === "completed" && Object.keys(categoryStatus).length === 0
  const missingCategories =
    overallStatus === "completed" && !legacyWithoutCategoryState
      ? Object.keys(CATEGORY_LABEL_MAP).filter(
          (category) => !["completed", "skipped", "failed"].includes(categoryStatus[category] ?? "")
        )
      : []
  const needsUpgrade = missingCategories.length > 0
  const isAbortPending = isAborting || cancelRequested
  const failureMessage = getPipelineFailureMessage(t, failureReason)

  const suspiciousDeliverablesEmpty = useMemo(() => {
    if (deliverablesStatus !== "completed" || bidDeliverables.length > 0) return false

    const deliverablesContext = [
      procurementOverview?.description ?? "",
      ...(procurementOverview?.highlights ?? []),
      ...requirements.map((requirement) => requirement.description),
    ]
      .join(" ")
      .toLowerCase()

    const suspiciousKeywords = [
      "anbud ska innehålla",
      "ska innehålla",
      "ska bifogas",
      "ska lämnas in",
      "kravmatris",
      "kompetensmatris",
      "cv",
      "timpris",
      "prisbilaga",
      "motivering",
    ]

    return suspiciousKeywords.some((keyword) => deliverablesContext.includes(keyword))
  }, [bidDeliverables.length, deliverablesStatus, procurementOverview, requirements])

  const deliverablesEmptyTitle = useMemo(
    () =>
      suspiciousDeliverablesEmpty
        ? t("tender.extraction.deliverablesEmptyTitleSuspicious")
        : t("tender.extraction.deliverablesEmptyTitle"),
    [suspiciousDeliverablesEmpty, t]
  )
  const deliverablesEmptyDescription = useMemo(
    () =>
      suspiciousDeliverablesEmpty
        ? t("tender.extraction.deliverablesEmptyDescriptionSuspicious")
        : t("tender.extraction.deliverablesEmptyDescription"),
    [suspiciousDeliverablesEmpty, t]
  )
  const deliverablesRetryCategory = suspiciousDeliverablesEmpty ? "bid_deliverables" : undefined
  const deliverablesEmptyTone = suspiciousDeliverablesEmpty ? "warning" : "default"

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">{t("tender.sections.extraction.title")}</h3>
          <p className="text-muted-foreground text-sm">
            {t("tender.sections.extraction.subtitle")}
          </p>
        </div>
        {overallStatus === "partial" && (
          <Button size="sm" variant="outline" onClick={() => setTraceModalOpen(true)}>
            <FileSearch className="mr-2 size-4" />
            {t("tender.extraction.llmTraceButton")}
          </Button>
        )}
        {overallStatus === "completed" && (
          <div className="flex items-center gap-2">
            <Button
              disabled={isExportingPm || categoryStatus.pm_content !== "completed"}
              size="sm"
              variant="outline"
              onClick={async () => {
                setIsExportingPm(true)
                try {
                  await downloadPmDocx(caseId, runId, caseTitle)
                } finally {
                  setIsExportingPm(false)
                }
              }}
            >
              {isExportingPm ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <FileText className="mr-2 size-4" />
              )}
              {t("tender.extraction.exportPm")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void downloadExtractionDocx(caseId, runId, caseTitle)}
            >
              <FileDown className="mr-2 size-4" />
              {t("tender.extraction.exportDocx")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button aria-label={t("tender.extraction.moreActions")} size="sm" variant="outline">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={isDownloadingSetup}
                  onClick={async () => {
                    setIsDownloadingSetup(true)
                    try {
                      await downloadSetupFilesZip(caseId, runId, caseTitle)
                    } finally {
                      setIsDownloadingSetup(false)
                    }
                  }}
                >
                  {isDownloadingSetup ? (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    <FileDown className="mr-2 size-4" />
                  )}
                  {t("tender.extraction.downloadSetupFilesZip")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTraceModalOpen(true)}>
                  <FileSearch className="mr-2 size-4" />
                  {t("tender.extraction.llmTraceButton")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={timingLoading}
                  onClick={async () => {
                    setTimingLoading(true)
                    try {
                      const data = await fetchExtractionTiming(caseId, runId)
                      setTimingMetrics(data.phaseMetrics as ExtractionTimingMetric[])
                      setTimingVersion(data.pipelineVersion)
                      setTimingModalOpen(true)
                    } finally {
                      setTimingLoading(false)
                    }
                  }}
                >
                  {timingLoading ? (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    <Clock className="mr-2 size-4" />
                  )}
                  Timing
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        {overallStatus === "processing" && canEdit && onAbort && (
          <div className="flex items-center gap-2">
            {isAbortPending ? (
              <Button disabled size="sm" variant="outline">
                <Loader2 className="mr-2 size-4 animate-spin" />
                {t("tender.abort.pending")}
              </Button>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    className="hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive"
                    size="sm"
                    variant="outline"
                  >
                    <StopCircle className="mr-2 size-4" />
                    {t("tender.abort.action")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("tender.abort.extraction.title")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("tender.abort.extraction.description")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("tender.abort.keepRunning")}</AlertDialogCancel>
                    <AlertDialogAction onClick={onAbort}>
                      {t("tender.abort.confirm")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </div>

      <TenderAiDisclaimer />

      <LlmTraceModal
        caseId={caseId}
        caseTitle={caseTitle}
        open={traceModalOpen}
        runId={runId}
        step="extraction"
        stepLabel={t("tender.sections.extraction.title")}
        onOpenChange={setTraceModalOpen}
      />

      <Dialog open={timingModalOpen} onOpenChange={setTimingModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="size-5" />
              Pipeline Timing
              {timingVersion && (
                <Badge className="ml-2" variant="outline">
                  {timingVersion}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {timingMetrics && timingMetrics.length > 0 ? (
            <div className="max-h-[60vh] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Phase</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Items</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {timingMetrics.map((metric, index) => {
                    const isTotalRow = metric.phase === "pipeline_total"
                    const ms = metric.durationMs
                    const durationStr = formatTimingDuration(ms)
                    const colorClass = timingDurationColor(ms)
                    return (
                      <TableRow
                        key={`${metric.phase}-${index}`}
                        className={isTotalRow ? "border-t-2 font-semibold" : ""}
                      >
                        <TableCell className="font-medium">{metric.phase}</TableCell>
                        <TableCell>
                          <span className={colorClass}>{durationStr}</span>
                        </TableCell>
                        <TableCell>{metric.outcome}</TableCell>
                        <TableCell>
                          {typeof metric.itemCount === "number" ? metric.itemCount : "—"}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No timing data available.</p>
          )}
        </DialogContent>
      </Dialog>

      {panelVisible && (
        <div
          className={cn(
            "transition-opacity duration-700",
            (overallStatus === "completed" || overallStatus === "partial") && "opacity-0"
          )}
        >
          <ExtractionProgressPanel
            categoryStatus={categoryStatus}
            hasOverview={!!procurementOverview}
            overallStatus={overallStatus ?? "processing"}
            rateLimitRetry={rateLimitRetry ?? undefined}
          />
        </div>
      )}

      {/* Full failure banner — shown when the pipeline failed entirely */}
      {overallStatus === "failed" && (
        <div className="border-destructive bg-destructive/[0.04] rounded-lg border p-5">
          <div className="flex items-start gap-3">
            <XCircle className="text-destructive mt-0.5 size-5 shrink-0" />
            <div className="flex-1 space-y-1">
              <p className="text-destructive text-sm font-semibold">
                {t("tender.extraction.failedTitle")}
              </p>
              <p className="text-muted-foreground text-xs">{failureMessage}</p>
            </div>
            {canEdit && onRetryExtraction && (
              <Button size="sm" variant="outline" onClick={onRetryExtraction}>
                <RefreshCw className="mr-2 size-4" />
                {t("tender.extraction.retryExtraction")}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* A run from before per-category tracking has no map at all: the retry
          route needs classification data, so the only recovery is a full rerun. */}
      {legacyWithoutCategoryState && (
        <div className="bg-muted/40 rounded-lg border p-4">
          <div className="flex items-start gap-3">
            <RefreshCw className="text-muted-foreground mt-0.5 size-4 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">{t("tender.extraction.upgradeLegacyTitle")}</p>
              <p className="text-muted-foreground mt-1 text-xs">
                {t("tender.extraction.upgradeLegacyHint")}
              </p>
            </div>
            {canEdit && onRetryExtraction && (
              <Button size="sm" variant="outline" onClick={onRetryExtraction}>
                <RefreshCw className="mr-2 size-4" />
                {t("tender.extraction.retryExtraction")}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Upgrade banner — an older completed run lacks categories the current
          pipeline always writes (or left them non-terminal); add them without
          redoing the whole analysis. */}
      {needsUpgrade && (
        <div className="bg-muted/40 rounded-lg border p-4">
          <div className="flex items-start gap-3">
            <RefreshCw className="text-muted-foreground mt-0.5 size-4 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">
                {t("tender.extraction.upgradeTitle", {
                  list: missingCategories
                    .map((cat) => (CATEGORY_LABEL_MAP as Record<string, string>)[cat] ?? cat)
                    .join(", "),
                })}
              </p>
              <p className="text-muted-foreground mt-1 text-xs">
                {t("tender.extraction.upgradeHint")}
              </p>
            </div>
            {canEdit && onRetryCategories && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRetryCategories(missingCategories)}
              >
                <RefreshCw className="mr-2 size-4" />
                {t("tender.extraction.upgradeAction")}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Partial failure banner — successful categories remain usable and the failed ones can be retried. */}
      {hasPartialFailure && (
        <div className="border-warning bg-warning/[0.04] rounded-lg border p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-warning mt-0.5 size-4 shrink-0" />
            <div className="flex-1">
              <p className="text-warning-foreground text-sm font-medium">
                {retryCategories.length === 1
                  ? t("tender.extraction.partialFailedOne", {
                      list: retryCategories
                        .map((cat) => (CATEGORY_LABEL_MAP as Record<string, string>)[cat] ?? cat)
                        .join(", "),
                    })
                  : t("tender.extraction.partialFailedMany", {
                      count: retryCategories.length,
                      list: retryCategories
                        .map((cat) => (CATEGORY_LABEL_MAP as Record<string, string>)[cat] ?? cat)
                        .join(", "),
                    })}
              </p>
            </div>
            {canEdit && onRetryCategories && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRetryCategories(retryCategories)}
              >
                <RefreshCw className="mr-2 size-4" />
                {t("tender.extraction.retryFailedCategories")}
              </Button>
            )}
          </div>
        </div>
      )}

      {procurementOverview && (
        <SnabbanalysCard
          overview={procurementOverview}
          questionsDeadline={questionsDeadline}
          submissionDeadlineIsManual={submissionDeadline?.isManual}
        />
      )}

      <Tabs
        key={summaryReady ? "summary" : "default"}
        defaultValue={summaryReady ? "sammanfattning" : "krav"}
      >
        <TabsList className="flex-wrap">
          <TabsTrigger value="sammanfattning">
            {t("tender.extraction.tabs.summary")}
            {isLoading(summaryStatus) && <Loader2 className="ml-1 size-3 animate-spin" />}
            {summaryReady && <CheckCircle className="text-success ml-1 size-3" />}
          </TabsTrigger>
          <TabsTrigger value="krav">
            {t("tender.extraction.categories.requirements")}
            {isLoading(requirementsStatus) ? (
              <Loader2 className="ml-1 size-3 animate-spin" />
            ) : (
              <Badge
                className="rounded-full px-1.5 py-0 text-[10px] tabular-nums"
                variant="secondary"
              >
                {mandatoryCount}
                {meritingCount > 0 && (
                  <span className="text-muted-foreground">+{meritingCount}</span>
                )}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="rollkrav">
            {t("tender.extraction.tabs.roles")}
            {isLoading(rolesStatus) ? (
              <Loader2 className="ml-1 size-3 animate-spin" />
            ) : (
              <Badge
                className="rounded-full px-1.5 py-0 text-[10px] tabular-nums"
                variant="secondary"
              >
                {roleRequirements.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="kriterier">
            {t("tender.extraction.categories.evaluation_criteria")}
            {isLoading(criteriaStatus) ? (
              <Loader2 className="ml-1 size-3 animate-spin" />
            ) : (
              <Badge
                className="rounded-full px-1.5 py-0 text-[10px] tabular-nums"
                variant="secondary"
              >
                {evaluationCriteria.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="villkor">
            {t("tender.extraction.categories.contract_terms")}
            {isLoading(termsStatus) ? (
              <Loader2 className="ml-1 size-3 animate-spin" />
            ) : (
              <Badge
                className="rounded-full px-1.5 py-0 text-[10px] tabular-nums"
                variant="secondary"
              >
                {contractTerms.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="anbudsinnehall">
            {t("tender.extraction.tabs.deliverables")}
            {isLoading(deliverablesStatus) ? (
              <Loader2 className="ml-1 size-3 animate-spin" />
            ) : (
              <Badge
                className="rounded-full px-1.5 py-0 text-[10px] tabular-nums"
                variant="secondary"
              >
                {bidDeliverables.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="dokumentlista">
            {t("tender.extraction.tabs.deliverableDocuments")}
            {isLoading(deliverableDocumentsStatus) ? (
              <Loader2 className="ml-1 size-3 animate-spin" />
            ) : (
              <Badge
                className="rounded-full px-1.5 py-0 text-[10px] tabular-nums"
                variant="secondary"
              >
                {deliverableDocuments.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="tidslinje">
            {t("tender.extraction.tabs.timeline")}
            {isLoading(timelineStatus) ? (
              <Loader2 className="ml-1 size-3 animate-spin" />
            ) : (
              <Badge
                className="rounded-full px-1.5 py-0 text-[10px] tabular-nums"
                variant="secondary"
              >
                {timeline.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="datum">
            {t("tender.extraction.tabs.dates")}
            {isLoading(datesStatus) ? (
              <Loader2 className="ml-1 size-3 animate-spin" />
            ) : (
              <Badge
                className="rounded-full px-1.5 py-0 text-[10px] tabular-nums"
                variant="secondary"
              >
                {criticalDates.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Ska-krav */}
        <TabsContent value="krav">
          <div className="mb-2 flex justify-end">
            <TabRegenerateButton
              canEdit={canEdit}
              category="requirements"
              status={requirementsStatus}
              onRetry={onRetryCategories}
            />
          </div>
          {requirementsStatus === "failed" && (
            <CategoryFailureBanner
              canEdit={canEdit}
              caseId={caseId}
              category="requirements"
              categoryLabel={CATEGORY_LABEL_MAP.requirements}
              errorMessage={categoryErrors.requirements}
              retryCount={categoryRetryCount.requirements ?? 0}
              runId={runId}
              onRetry={onRetryCategories}
            />
          )}
          {requirements.length > 0 && (
            <div className="divide-border divide-y overflow-hidden rounded-lg border">
              <div className="bg-muted/40 text-muted-foreground grid grid-cols-[72px_1fr_minmax(0,280px)_96px] gap-3 px-5 py-3 text-xs font-medium tracking-wide uppercase">
                <span>{t("tender.extraction.tableHeaders.id")}</span>
                <span>{t("tender.extraction.tableHeaders.requirement")}</span>
                <span>{t("tender.extraction.tableHeaders.source")}</span>
                <span>{t("tender.extraction.tableHeaders.confidence")}</span>
              </div>
              {requirements.map((req) => {
                const conf = CONFIDENCE_CONFIG[req.confidence]
                const isMeriting = !req.isMandatory
                return (
                  <div
                    key={req.id}
                    className={cn(
                      "grid grid-cols-[72px_1fr_minmax(0,280px)_96px] items-start gap-3 px-5 py-4",
                      isMeriting && "bg-muted/20"
                    )}
                  >
                    <span className="text-muted-foreground font-mono text-xs font-medium">
                      {req.id}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm leading-snug">{req.description}</p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        <Badge variant="secondary">{req.category}</Badge>
                        {isMeriting && (
                          <Badge
                            className="border-warning bg-warning/10 text-warning-foreground"
                            variant="outline"
                          >
                            <Star className="mr-1 size-3" />
                            {t("tender.extraction.meritingBadge")}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="min-w-0">
                      {req.snippet ? (
                        <SnippetPopover
                          documentUrl={getSnippetDocumentUrl(req.snippet)}
                          reasoning={req.reasoning}
                          snippet={req.snippet}
                          onOpenPreview={
                            req.snippet.kind === "file"
                              ? () => openDocumentPreview(req.snippet!)
                              : undefined
                          }
                        />
                      ) : (
                        <SourceRefDisplay
                          source={req.source}
                          onOpenPreview={
                            req.source.kind === "file"
                              ? () => openDocumentPreview(req.source)
                              : undefined
                          }
                        />
                      )}
                    </div>
                    <Badge
                      className={cn("w-fit border font-normal", conf.className)}
                      variant="outline"
                    >
                      {req.confidence === "saker" ? (
                        <CheckCircle className="mr-1 size-3" />
                      ) : (
                        <AlertTriangle className="mr-1 size-3" />
                      )}
                      {t(conf.labelKey)}
                    </Badge>
                  </div>
                )
              })}
            </div>
          )}
          {requirements.length === 0 && requirementsStatus === "completed" && (
            <ExtractionEmptyState
              description={t("tender.extraction.emptyRequirementsDescription")}
              title={t("tender.extraction.emptyRequirementsTitle")}
            />
          )}
        </TabsContent>

        {/* Utvärderingskriterier */}
        <TabsContent value="kriterier">
          <div className="mb-2 flex justify-end">
            <TabRegenerateButton
              canEdit={canEdit}
              category="evaluation_criteria"
              status={criteriaStatus}
              onRetry={onRetryCategories}
            />
          </div>
          {criteriaStatus === "failed" && (
            <CategoryFailureBanner
              canEdit={canEdit}
              caseId={caseId}
              category="evaluation_criteria"
              categoryLabel={CATEGORY_LABEL_MAP.evaluation_criteria}
              errorMessage={categoryErrors.evaluation_criteria}
              retryCount={categoryRetryCount.evaluation_criteria ?? 0}
              runId={runId}
              onRetry={onRetryCategories}
            />
          )}
          {evaluationCriteria.length > 0 && (
            <div className="divide-border divide-y overflow-hidden rounded-lg border">
              <div className="bg-muted/40 text-muted-foreground grid grid-cols-[1fr_1fr_minmax(0,280px)] gap-3 px-5 py-3 text-xs font-medium tracking-wide uppercase">
                <span>{t("tender.extraction.tableHeaders.criterion")}</span>
                <span>{t("tender.extraction.tableHeadersDescription")}</span>
                <span>{t("tender.extraction.tableHeaders.source")}</span>
              </div>
              {evaluationCriteria.map((crit) => (
                <div
                  key={crit.id}
                  className="grid grid-cols-[1fr_1fr_minmax(0,280px)] items-start gap-3 px-5 py-4"
                >
                  <p className="min-w-0 text-sm font-medium">{crit.name}</p>
                  <p className="text-muted-foreground min-w-0 text-sm">{crit.description}</p>
                  <div className="min-w-0">
                    {crit.snippet ? (
                      <SnippetPopover
                        documentUrl={getSnippetDocumentUrl(crit.snippet)}
                        reasoning={crit.reasoning}
                        snippet={crit.snippet}
                        onOpenPreview={
                          crit.snippet.kind === "file"
                            ? () => openDocumentPreview(crit.snippet!)
                            : undefined
                        }
                      />
                    ) : (
                      <SourceRefDisplay
                        source={crit.source}
                        onOpenPreview={
                          crit.source.kind === "file"
                            ? () => openDocumentPreview(crit.source)
                            : undefined
                        }
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {evaluationCriteria.length === 0 && criteriaStatus === "completed" && (
            <ExtractionEmptyState
              description={t("tender.extraction.emptyCriteriaDescription")}
              title={t("tender.extraction.emptyCriteriaTitle")}
            />
          )}
        </TabsContent>

        {/* Rollkrav */}
        <TabsContent value="rollkrav">
          <div className="mb-2 flex justify-end">
            <TabRegenerateButton
              canEdit={canEdit}
              category="role_requirements"
              status={rolesStatus}
              onRetry={onRetryCategories}
            />
          </div>
          <div className="border-accent bg-accent/[0.04] mb-4 rounded-lg border p-4">
            <div className="flex items-start gap-3">
              <Info className="text-accent mt-0.5 size-4 shrink-0" />
              <p className="text-muted-foreground text-sm">
                {t("tender.extraction.rolesInfoBanner")}
              </p>
            </div>
          </div>
          {rolesStatus === "failed" && (
            <CategoryFailureBanner
              canEdit={canEdit}
              caseId={caseId}
              category="role_requirements"
              categoryLabel={CATEGORY_LABEL_MAP.role_requirements}
              errorMessage={categoryErrors.role_requirements}
              retryCount={categoryRetryCount.role_requirements ?? 0}
              runId={runId}
              onRetry={onRetryCategories}
            />
          )}
          {roleRequirements.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              {roleRequirements.map((role) => (
                <div key={role.id} className="space-y-3 rounded-lg border p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold">{role.roleName}</p>
                      {role.quantity > 1 && (
                        <p className="text-muted-foreground text-xs">
                          {t("tender.extraction.roleQuantityPeople", { count: role.quantity })}
                        </p>
                      )}
                    </div>
                    <Badge className="shrink-0 text-xs" variant="secondary">
                      {t("tender.extraction.roleQuantityBadge", { count: role.quantity })}
                    </Badge>
                  </div>

                  <div className="space-y-1.5 text-xs">
                    {role.educationLevel && (
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">
                          {t("tender.extraction.roleFieldEducation")}
                        </span>
                        <span className="text-right font-medium">{role.educationLevel}</span>
                      </div>
                    )}
                    {role.minYearsExperience != null && (
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">
                          {t("tender.extraction.roleFieldMinExperience")}
                        </span>
                        <span className="font-medium">
                          {t("tender.extraction.roleYearsSuffix", {
                            count: role.minYearsExperience,
                          })}
                        </span>
                      </div>
                    )}
                  </div>

                  {role.requiredCertifications.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-muted-foreground text-xs font-medium">
                        {t("tender.extraction.roleCertsHeading")}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {role.requiredCertifications.map((c) => (
                          <Badge key={c} className="text-xs" variant="outline">
                            {c}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {role.requiredExperience.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-muted-foreground text-xs font-medium">
                        {t("tender.extraction.roleExperienceHeading")}
                      </p>
                      <ul className="space-y-0.5">
                        {role.requiredExperience.map((e) => (
                          <li key={e} className="flex items-start gap-1 text-xs">
                            <span className="text-muted-foreground mt-0.5">·</span>
                            <span>{e}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {role.meritingQualifications.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-warning-foreground text-xs font-medium">
                        {t("tender.extraction.roleMeritingHeading")}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {role.meritingQualifications.map((m) => (
                          <Badge
                            key={m}
                            className="border-warning bg-warning/10 text-warning-foreground text-xs"
                            variant="outline"
                          >
                            <Star className="mr-1 size-3 shrink-0" />
                            {m}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {role.snippet && (
                    <div className="border-t pt-1">
                      <SnippetPopover
                        documentUrl={getSnippetDocumentUrl(role.snippet)}
                        reasoning={role.reasoning}
                        snippet={role.snippet}
                        onOpenPreview={
                          role.snippet.kind === "file"
                            ? () => openDocumentPreview(role.snippet!)
                            : undefined
                        }
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {roleRequirements.length === 0 && rolesStatus === "completed" && (
            <ExtractionEmptyState
              description={t("tender.extraction.emptyRolesDescription")}
              title={t("tender.extraction.emptyRolesTitle")}
            />
          )}
        </TabsContent>

        {/* Avtalsvillkor */}
        <TabsContent value="villkor">
          <div className="mb-2 flex justify-end">
            <TabRegenerateButton
              canEdit={canEdit}
              category="contract_terms"
              status={termsStatus}
              onRetry={onRetryCategories}
            />
          </div>
          <div className="border-accent bg-accent/[0.04] mb-4 rounded-lg border p-4">
            <div className="flex items-start gap-3">
              <Info className="text-accent mt-0.5 size-4 shrink-0" />
              <p className="text-muted-foreground text-sm">
                {t("tender.extraction.termsInfoBanner")}
              </p>
            </div>
          </div>
          {termsStatus === "failed" && (
            <CategoryFailureBanner
              canEdit={canEdit}
              caseId={caseId}
              category="contract_terms"
              categoryLabel={CATEGORY_LABEL_MAP.contract_terms}
              errorMessage={categoryErrors.contract_terms}
              retryCount={categoryRetryCount.contract_terms ?? 0}
              runId={runId}
              onRetry={onRetryCategories}
            />
          )}
          {contractTerms.length > 0 && (
            <div className="divide-border divide-y overflow-hidden rounded-lg border">
              <div className="bg-muted/40 text-muted-foreground grid grid-cols-[150px_1fr_76px_minmax(0,280px)] gap-3 px-5 py-3 text-xs font-medium tracking-wide uppercase">
                <span>{t("tender.extraction.tableHeadersClause")}</span>
                <span>{t("tender.extraction.tableHeadersDescription")}</span>
                <span>{t("tender.extraction.tableHeaders.riskLevel")}</span>
                <span>{t("tender.extraction.tableHeaders.source")}</span>
              </div>
              {contractTerms.map((term) => {
                const risk = RISK_CONFIG[term.riskLevel]
                return (
                  <div
                    key={term.id}
                    className={cn(
                      "grid grid-cols-[150px_1fr_76px_minmax(0,280px)] items-start gap-3 px-5 py-4",
                      term.flagged && "bg-destructive/[0.03]"
                    )}
                  >
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-start gap-1.5">
                        {term.flagged && (
                          <FileWarning className="text-destructive mt-0.5 size-4 shrink-0" />
                        )}
                        <p className="min-w-0 text-sm font-medium">{term.clause}</p>
                      </div>
                      {term.frameworkDeviation && (
                        <Badge
                          className="border-primary-ink bg-primary/10 text-primary-ink w-fit text-xs"
                          title={term.frameworkDeviation.deviationDescription}
                          variant="outline"
                        >
                          {t("tender.extraction.deviationBadge", {
                            standard: term.frameworkDeviation.standard,
                          })}
                        </Badge>
                      )}
                    </div>
                    <div className="min-w-0 space-y-1">
                      <p className="text-muted-foreground text-sm">{term.description}</p>
                      {term.frameworkDeviation && (
                        <p className="text-primary-ink/80 text-xs italic">
                          {term.frameworkDeviation.deviationDescription}
                        </p>
                      )}
                    </div>
                    <Badge
                      className={cn("w-fit border font-normal", risk.className)}
                      variant="outline"
                    >
                      {t(risk.labelKey)}
                    </Badge>
                    <div className="min-w-0">
                      {term.snippet ? (
                        <SnippetPopover
                          documentUrl={getSnippetDocumentUrl(term.snippet)}
                          reasoning={term.reasoning}
                          snippet={term.snippet}
                          onOpenPreview={
                            term.snippet.kind === "file"
                              ? () => openDocumentPreview(term.snippet!)
                              : undefined
                          }
                        />
                      ) : (
                        <SourceRefDisplay
                          source={term.source}
                          onOpenPreview={
                            term.source.kind === "file"
                              ? () => openDocumentPreview(term.source)
                              : undefined
                          }
                        />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {contractTerms.length === 0 && termsStatus === "completed" && (
            <ExtractionEmptyState
              description={t("tender.extraction.emptyTermsDescription")}
              title={t("tender.extraction.emptyTermsTitle")}
            />
          )}
        </TabsContent>

        {/* Anbudsinnehåll */}
        <TabsContent value="anbudsinnehall">
          <div className="mb-2 flex justify-end">
            <TabRegenerateButton
              canEdit={canEdit}
              category="bid_deliverables"
              status={deliverablesStatus}
              onRetry={onRetryCategories}
            />
          </div>
          {deliverablesStatus === "failed" && (
            <CategoryFailureBanner
              canEdit={canEdit}
              caseId={caseId}
              category="bid_deliverables"
              categoryLabel={CATEGORY_LABEL_MAP.bid_deliverables}
              errorMessage={categoryErrors.bid_deliverables}
              retryCount={categoryRetryCount.bid_deliverables ?? 0}
              runId={runId}
              onRetry={onRetryCategories}
            />
          )}
          {bidDeliverables.length > 0 && (
            <>
              <div className="divide-border divide-y overflow-hidden rounded-lg border">
                <div className="bg-muted/40 text-muted-foreground grid grid-cols-[1fr_80px_minmax(0,280px)] gap-3 px-5 py-3 text-xs font-medium tracking-wide uppercase">
                  <span>{t("tender.extraction.tableHeaders.deliverable")}</span>
                  <span>{t("tender.extraction.tableHeadersType")}</span>
                  <span>{t("tender.extraction.tableHeaders.source")}</span>
                </div>
                {bidDeliverables.map((item) => (
                  <div
                    key={item.id}
                    className="grid grid-cols-[1fr_80px_minmax(0,280px)] items-start gap-3 px-5 py-4"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{item.name}</p>
                      <p className="text-muted-foreground mt-0.5 text-xs">{item.description}</p>
                    </div>
                    <Badge
                      variant="outline"
                      className={cn(
                        "w-fit border text-xs font-normal",
                        item.isRequired
                          ? "border-border bg-muted/50 text-foreground"
                          : "border-border bg-muted/50 text-muted-foreground"
                      )}
                    >
                      {item.isRequired
                        ? t("tender.extraction.deliverableMandatory")
                        : t("tender.extraction.deliverableVoluntary")}
                      {item.submissionStage !== "bid" && item.submissionStage !== "unknown" && (
                        <> · {t(`tender.extraction.submissionStage.${item.submissionStage}`)}</>
                      )}
                    </Badge>
                    {item.condition && (
                      <p className="text-muted-foreground text-xs">
                        {t("tender.extraction.submissionCondition")}: {item.condition}
                      </p>
                    )}
                    <div className="min-w-0">
                      {item.snippet ? (
                        <SnippetPopover
                          documentUrl={getSnippetDocumentUrl(item.snippet)}
                          reasoning={item.reasoning}
                          snippet={item.snippet}
                          onOpenPreview={
                            item.snippet.kind === "file"
                              ? () => openDocumentPreview(item.snippet!)
                              : undefined
                          }
                        />
                      ) : (
                        <SourceRefDisplay
                          source={item.source}
                          onOpenPreview={
                            item.source.kind === "file"
                              ? () => openDocumentPreview(item.source)
                              : undefined
                          }
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {bidDeliverables.length === 0 && deliverablesStatus === "completed" && (
            <ExtractionEmptyState
              canEdit={canEdit}
              description={deliverablesEmptyDescription}
              retryCategory={deliverablesRetryCategory}
              title={deliverablesEmptyTitle}
              tone={deliverablesEmptyTone}
              onRetryCategories={onRetryCategories}
            />
          )}
        </TabsContent>

        {/* Viktiga datum */}
        <TabsContent value="datum">
          <div className="mb-2 flex justify-end">
            <TabRegenerateButton
              canEdit={canEdit}
              category="critical_dates"
              status={datesStatus}
              onRetry={onRetryCategories}
            />
          </div>
          {datesStatus === "failed" && (
            <CategoryFailureBanner
              canEdit={canEdit}
              caseId={caseId}
              category="critical_dates"
              categoryLabel={CATEGORY_LABEL_MAP.critical_dates}
              errorMessage={categoryErrors.critical_dates}
              retryCount={categoryRetryCount.critical_dates ?? 0}
              runId={runId}
              onRetry={onRetryCategories}
            />
          )}
          <div
            className={cn(
              "mb-4 rounded-lg border p-4",
              submissionDeadline?.requiresManualEntry
                ? "border-warning bg-warning/[0.04]"
                : "bg-card"
            )}
          >
            <div className="flex flex-col gap-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">
                      {t("tender.extraction.submissionDeadlineHeading")}
                    </p>
                    {submissionDeadline?.isManual ? (
                      <Badge
                        className="border-warning bg-warning/10 text-warning-foreground"
                        variant="outline"
                      >
                        {t("tender.extraction.manualDeadlineBadge")}
                      </Badge>
                    ) : null}
                  </div>
                  <p
                    className={cn(
                      "text-lg font-semibold",
                      submissionDeadline?.requiresManualEntry && "text-warning-foreground"
                    )}
                  >
                    {submissionDeadline?.effectiveValue
                      ? formatDeadline(submissionDeadline.effectiveValue, i18n.language)
                      : t("tender.extraction.missingValue")}
                  </p>
                  {submissionDeadline?.requiresManualEntry ? (
                    <p className="text-warning-foreground text-sm">
                      {t("tender.extraction.submissionDeadlineManualWarning")}
                    </p>
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      {t("tender.extraction.submissionDeadlineHint")}
                    </p>
                  )}
                  {submissionDeadline?.isManual && submissionDeadline.extractedValue ? (
                    <p className="text-muted-foreground text-xs">
                      {t("tender.extraction.deadline.extractedValueLabel")}{" "}
                      {formatDeadline(submissionDeadline.extractedValue, i18n.language)}
                    </p>
                  ) : null}
                  {submissionDeadline?.override?.reason ? (
                    <p className="text-muted-foreground text-xs">
                      {t("tender.extraction.deadline.reasonPrefix")}{" "}
                      {submissionDeadline.override.reason}
                    </p>
                  ) : null}
                  {submissionDeadline?.override?.changedAt ? (
                    <p className="text-muted-foreground text-xs">
                      {t("tender.extraction.deadline.lastUpdated")}{" "}
                      {formatDeadline(submissionDeadline.override.changedAt, i18n.language)}
                      {submissionDeadline.override.changedByDisplayName
                        ? t("tender.extraction.deadline.updatedBySuffix", {
                            name: submissionDeadline.override.changedByDisplayName,
                          })
                        : ""}
                    </p>
                  ) : null}
                </div>
                {canEdit && onUpdateSubmissionDeadline ? (
                  <div className="flex shrink-0 items-center gap-2">
                    {!isEditingSubmissionDeadline ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleStartSubmissionDeadlineEdit}
                        >
                          {submissionDeadline?.isManual
                            ? t("tender.extraction.deadline.editChange")
                            : t("tender.extraction.deadline.editManual")}
                        </Button>
                        {submissionDeadline?.isManual ? (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                disabled={isUpdatingSubmissionDeadline}
                                size="sm"
                                variant="ghost"
                              >
                                {t("tender.extraction.deadline.reset")}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {t("tender.extraction.resetDeadline.title")}
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  {t("tender.extraction.deadline.resetDescription")}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t("tender.dialogs.cancel")}</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => void handleClearSubmissionDeadline()}
                                >
                                  {t("tender.extraction.deadline.reset")}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {isEditingSubmissionDeadline ? (
                <div className="space-y-3 rounded-md border border-dashed p-3">
                  <div className="grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
                    <div className="space-y-1">
                      <label className="text-xs font-medium" htmlFor="submission-deadline-input">
                        {t("tender.extraction.deadline.dateLabel")}
                      </label>
                      <Input
                        id="submission-deadline-input"
                        type="date"
                        value={submissionDeadlineDraft}
                        onChange={(event) => setSubmissionDeadlineDraft(event.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium" htmlFor="submission-deadline-reason">
                        {t("tender.extraction.deadline.reasonLabel")}
                      </label>
                      <Input
                        id="submission-deadline-reason"
                        placeholder={t("tender.extraction.deadline.reasonPlaceholder")}
                        value={submissionDeadlineReason}
                        onChange={(event) => setSubmissionDeadlineReason(event.target.value)}
                      />
                    </div>
                  </div>
                  {submissionDeadlineError ? (
                    <p className="text-destructive text-sm">{submissionDeadlineError}</p>
                  ) : null}
                  <div className="flex items-center gap-2">
                    <Button
                      disabled={isUpdatingSubmissionDeadline}
                      size="sm"
                      onClick={() => void handleSaveSubmissionDeadline()}
                    >
                      {isUpdatingSubmissionDeadline ? (
                        <Loader2 className="mr-1.5 size-4 animate-spin" />
                      ) : null}
                      {t("tender.extraction.deadline.saveManual")}
                    </Button>
                    <Button
                      disabled={isUpdatingSubmissionDeadline}
                      size="sm"
                      variant="ghost"
                      onClick={handleCancelSubmissionDeadlineEdit}
                    >
                      {t("tender.dialogs.cancel")}
                    </Button>
                  </div>
                </div>
              ) : null}

              {submissionDeadline && submissionDeadline.history.length > 0 ? (
                <details className="text-sm">
                  <summary className="text-muted-foreground cursor-pointer text-xs select-none">
                    {t("tender.extraction.deadline.showHistory", {
                      count: submissionDeadline.history.length,
                    })}
                  </summary>
                  <div className="mt-3 space-y-2">
                    {submissionDeadline.history
                      .slice()
                      .reverse()
                      .slice(0, 5)
                      .map((entry) => {
                        let actionLabel = t("tender.extraction.deadline.historyUpdated")
                        if (entry.action === "clear") {
                          actionLabel = t("tender.extraction.deadline.historyCleared")
                        } else if (entry.action === "set") {
                          actionLabel = t("tender.extraction.deadline.historySet")
                        }

                        return (
                          <div key={entry.id} className="rounded-md border px-3 py-2">
                            <p className="font-medium">{actionLabel}</p>
                            <p className="text-muted-foreground text-xs">
                              {entry.value
                                ? formatDeadline(entry.value, i18n.language)
                                : t("tender.extraction.deadline.noManualTime")}
                              {entry.changedAt
                                ? ` · ${formatDeadline(entry.changedAt, i18n.language)}`
                                : ""}
                              {entry.changedByDisplayName
                                ? t("tender.extraction.deadline.historyByMarker", {
                                    name: entry.changedByDisplayName,
                                  })
                                : ""}
                            </p>
                            {entry.reason ? (
                              <p className="text-muted-foreground mt-1 text-xs">{entry.reason}</p>
                            ) : null}
                          </div>
                        )
                      })}
                  </div>
                </details>
              ) : null}
            </div>
          </div>
          {datesStatus === "completed" && (
            <>
              <CriticalDatesTabEditor
                canEdit={canEdit}
                criticalDates={criticalDates}
                extractedIds={extractedCriticalDateIds}
                getSnippetDocumentUrl={(s) => (s ? getSnippetDocumentUrl(s) : undefined)}
                isUpdating={updateCriticalDates.isPending}
                openDocumentPreview={openDocumentPreview}
                removedIds={criticalDatesRemovedIds}
                SnippetPopover={SnippetPopover}
                userRows={criticalDatesUserRows}
                onApply={async (payload) => {
                  await updateCriticalDates.mutateAsync({
                    runId,
                    removedIds: payload.removedIds,
                    userRows: payload.userRows,
                  })
                }}
              />
              {criticalDates.length === 0 ? (
                <ExtractionEmptyState
                  description={t("tender.extraction.emptyDatesDescription")}
                  title={t("tender.extraction.emptyDatesTitle")}
                />
              ) : null}
            </>
          )}
        </TabsContent>

        {/* PM (renamed from Sammanfattning — PM content; legacy bid_summary fallback) */}
        <TabsContent value="sammanfattning">
          <div className="mb-2 flex justify-end">
            <TabRegenerateButton
              canEdit={canEdit}
              category="pm_content"
              status={summaryStatus}
              onRetry={onRetryCategories}
            />
          </div>
          {summaryStatus === "failed" && (
            <CategoryFailureBanner
              canEdit={canEdit}
              caseId={caseId}
              category="pm_content"
              categoryLabel={t("tender.extraction.summaryExtractFailedTitle")}
              errorMessage={categoryErrors.pm_content}
              retryCount={categoryRetryCount.pm_content ?? 0}
              runId={runId}
              onRetry={onRetryCategories}
            />
          )}
          {hasPmContent && pmContent && (
            <PmContentSections
              contractTerms={contractTerms}
              deliverableDocuments={deliverableDocuments}
              evaluationCriteria={evaluationCriteria}
              extractionLookup={extractionLookup}
              openDocumentPreview={openDocumentPreview}
              pmContent={pmContent}
              procurementOverview={procurementOverview}
              questionsDeadline={questionsDeadline}
              roleRequirements={roleRequirements}
              timeline={timeline}
            />
          )}
          {!hasPmContent && summaryMarkdown && (
            <div className="prose dark:prose-invert prose-sm max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ children }) => (
                    <p>
                      <CitationText
                        as="span"
                        lookup={extractionLookup}
                        text={childrenToText(children)}
                        onOpenSource={(source) => openDocumentPreview(source)}
                      />
                    </p>
                  ),
                  li: ({ children }) => (
                    <li>
                      <CitationText
                        as="span"
                        lookup={extractionLookup}
                        text={childrenToText(children)}
                        onOpenSource={(source) => openDocumentPreview(source)}
                      />
                    </li>
                  ),
                }}
              >
                {summaryMarkdown}
              </ReactMarkdown>
            </div>
          )}
          {!hasPmContent && !summaryMarkdown && summaryStatus === "completed" && (
            <ExtractionEmptyState
              description={t("tender.extraction.emptySummaryDescription")}
              title={t("tender.extraction.emptySummaryTitle")}
            />
          )}
          {!hasPmContent &&
            !summaryMarkdown &&
            summaryStatus !== "completed" &&
            summaryStatus !== "failed" && (
              <div className="text-muted-foreground flex items-center gap-2 py-6 text-sm">
                {isLoading(summaryStatus) && <Loader2 className="size-4 animate-spin" />}
                {isLoading(summaryStatus)
                  ? t("tender.extraction.summaryLoadingHint")
                  : t("tender.extraction.summaryPendingHint")}
              </div>
            )}
        </TabsContent>

        {/* Dokumentlista — synthesized documents-to-hand-in derived from bid_deliverables */}
        <TabsContent value="dokumentlista">
          <div className="mb-2 flex justify-end">
            <TabRegenerateButton
              canEdit={canEdit}
              category="deliverable_documents"
              status={deliverableDocumentsStatus}
              onRetry={onRetryCategories}
            />
          </div>
          {deliverableDocumentsStatus === "failed" && (
            <CategoryFailureBanner
              canEdit={canEdit}
              caseId={caseId}
              category="deliverable_documents"
              categoryLabel={t("tender.extraction.categories.deliverable_documents")}
              errorMessage={categoryErrors.deliverable_documents}
              retryCount={categoryRetryCount.deliverable_documents ?? 0}
              runId={runId}
              onRetry={onRetryCategories}
            />
          )}
          {deliverableDocuments.length === 0 && isLoading(deliverableDocumentsStatus) && (
            <div className="text-muted-foreground flex items-center gap-2 py-6 text-sm">
              <Loader2 className="size-4 animate-spin" />
              {t("tender.extraction.summaryLoadingHint")}
            </div>
          )}
          {deliverableDocuments.length === 0 && !isLoading(deliverableDocumentsStatus) && (
            <ExtractionEmptyState
              description={t("tender.extraction.emptyDeliverablesDescription")}
              title={t("tender.extraction.emptyDeliverablesTitle")}
            />
          )}
          {deliverableDocuments.length > 0 && (
            <div className="space-y-4">
              {deliverableDocuments.map((doc) => (
                <div key={doc.id} className="bg-card rounded-lg border p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <h4 className="font-semibold">
                        {doc.documentName}
                        {!doc.isRequired && (
                          <Badge className="ml-2" variant="outline">
                            Frivilligt
                          </Badge>
                        )}
                        {doc.submissionStage !== "bid" && doc.submissionStage !== "unknown" && (
                          <Badge className="ml-2" variant="outline">
                            {t(`tender.extraction.submissionStage.${doc.submissionStage}`)}
                          </Badge>
                        )}
                      </h4>
                      {doc.description && (
                        <p className="text-muted-foreground mt-1 text-sm">{doc.description}</p>
                      )}
                      {doc.condition && (
                        <p className="text-muted-foreground mt-1 text-xs">
                          {t("tender.extraction.submissionCondition")}: {doc.condition}
                        </p>
                      )}
                    </div>
                  </div>
                  {doc.mustInclude.length > 0 && (
                    <ul className="mt-3 list-disc pl-5 text-sm">
                      {doc.mustInclude.map((item, i) => (
                        <li key={`${doc.id}-mi-${i}`}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tidslinje — synthesized chronological timeline derived from critical_dates */}
        <TabsContent value="tidslinje">
          <div className="mb-2 flex justify-end">
            <TabRegenerateButton
              canEdit={canEdit}
              category="timeline"
              status={timelineStatus}
              onRetry={onRetryCategories}
            />
          </div>
          {timelineStatus === "failed" && (
            <CategoryFailureBanner
              canEdit={canEdit}
              caseId={caseId}
              category="timeline"
              categoryLabel={t("tender.extraction.categories.timeline")}
              errorMessage={categoryErrors.timeline}
              retryCount={categoryRetryCount.timeline ?? 0}
              runId={runId}
              onRetry={onRetryCategories}
            />
          )}
          {timeline.length === 0 && isLoading(timelineStatus) && (
            <div className="text-muted-foreground flex items-center gap-2 py-6 text-sm">
              <Loader2 className="size-4 animate-spin" />
              {t("tender.extraction.summaryLoadingHint")}
            </div>
          )}
          {timeline.length === 0 && !isLoading(timelineStatus) && (
            <ExtractionEmptyState
              description={t("tender.extraction.emptyDatesDescription")}
              title={t("tender.extraction.emptyDatesTitle")}
            />
          )}
          {timeline.length > 0 && <TimelineView entries={timeline} />}
        </TabsContent>
      </Tabs>
    </div>
  )
}
